// IDB read/write helpers for the local-first BSB Bible data layer
// (M.3.6.3.b). Higher-level orchestration (manifest fetch, validation,
// progress reporting, single-flight dedupe) lives in `bsb-bootstrap.ts`.
// Keeping persistence here means the bootstrap module can be reasoned
// about without an IDB import surface and the IDB shape stays
// localized to one file.
//
// `hydrateAtomic` is the first multi-store readwrite transaction in the
// codebase. The established pattern in `db.ts` is single-store helpers
// (one `db.put` / `db.get` per call). The multi-store boundary is
// encapsulated entirely inside this one helper so the rest of the app
// keeps the simpler grammar.
//
// Cross-tab safety: IndexedDB serializes overlapping readwrite
// transactions by store scope. Two tabs each running `hydrateAtomic`
// execute sequentially; no tab observes a partial state. Worst case
// is duplicate work (both tabs fetch + write the same payload), which
// is acceptable for the launch posture.

import { getReadingAccessSnapshot } from './reading-access';
import { getDb, BSB_BOOKS_STORE, BSB_META_STORE } from '@/lib/db';
import { BibleHydrationError } from '@/lib/bsb-errors';
import type {
  BsbBlock,
  BsbBook,
  BsbInventory,
  BsbMeta,
  BsbVerse,
  TextSegment,
} from '@/lib/bsb-types';

const META_KEY = 'manifest' as const;
const INVENTORY_KEY = 'inventory' as const;

/** Public BSB-v1 cache in supported schemas 8–10; owner snapshots separately
 * require v10. This open never creates/upgrades a DB or reads owner stores. */
async function cachedBibleRead<T>(name: string, read: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('adia-db');
    request.onupgradeneeded = () => request.transaction?.abort();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
  try {
    if (![8, 9, 10].includes(db.version) || !db.objectStoreNames.contains(name)) {
      throw new Error('cached-bible-unavailable');
    }
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(name, 'readonly');
      const request = read(tx.objectStore(name));
      let value: T;
      request.onsuccess = () => { value = request.result; };
      tx.oncomplete = () => resolve(value);
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function getBsbBook(slug: string): Promise<BsbBook | undefined> {
  try {
    if (getReadingAccessSnapshot().restricted) return cachedBibleRead(BSB_BOOKS_STORE, (store) => store.get(slug));
    const db = await getDb();
    return db.get(BSB_BOOKS_STORE, slug);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'getBsbBook', slug },
    });
  }
}

// Single-book put for the requested-book-first fast path
// (bsb-bootstrap.ts:ensureBookAvailable, 2026-08-25). Deliberately a
// SINGLE-store write with no meta touch: partial per-book saves are
// cache warms only, and the meta row keeps meaning "the FULL corpus is
// hydrated" (the offline-availability signal). `hydrateAtomic` remains
// the only multi-store transaction in the codebase (rule #67); it
// clears this store wholesale, so per-book saves never leave a mixed
// old/new corpus past a successful full hydration.
export async function saveBsbBook(book: BsbBook): Promise<void> {
  try {
    const db = await getDb();
    await db.put(BSB_BOOKS_STORE, book);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'saveBsbBook', slug: book.slug },
    });
  }
}

export async function getBsbMeta(): Promise<BsbMeta | undefined> {
  try {
    if (getReadingAccessSnapshot().restricted) return cachedBibleRead(BSB_META_STORE, (store) => store.get(META_KEY));
    const db = await getDb();
    return (await db.get(BSB_META_STORE, META_KEY)) as BsbMeta | undefined;
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'getBsbMeta' },
    });
  }
}

// ---- Resumable first-hydration support (2026-08-25) ----
//
// All single-store writes; `hydrateAtomic` below remains the only
// multi-store transaction in the codebase (rule #67). The meta row is
// written SEPARATELY after the resumable path verifies all 66 books —
// a crash between the last book put and the meta put just means the
// next run re-verifies and writes it (convergent).

// Cheap completeness witness: the record count of the books store.
// Backs the verified "Available offline." claim without deserializing
// ~6 MB of book payloads.
export async function countBsbBooks(): Promise<number> {
  try {
    if (getReadingAccessSnapshot().restricted) return cachedBibleRead(BSB_BOOKS_STORE, (store) => store.count());
    const db = await getDb();
    return db.count(BSB_BOOKS_STORE);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'countBsbBooks' },
    });
  }
}

// Keys-only listing (no payload deserialization) so the resumable
// path can seed its checkpoint from what is actually on device in the
// meta-present repair case.
export async function getBsbBookSlugs(): Promise<string[]> {
  try {
    if (getReadingAccessSnapshot().restricted) return cachedBibleRead(BSB_BOOKS_STORE, (store) => store.getAllKeys()) as Promise<string[]>;
    const db = await getDb();
    return (await db.getAllKeys(BSB_BOOKS_STORE)) as string[];
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'getBsbBookSlugs' },
    });
  }
}

export async function saveBsbMeta(meta: BsbMeta): Promise<void> {
  try {
    const db = await getDb();
    await db.put(BSB_META_STORE, meta);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'saveBsbMeta' },
    });
  }
}

export async function getBsbInventory(): Promise<BsbInventory | undefined> {
  try {
    if (getReadingAccessSnapshot().restricted) return cachedBibleRead(BSB_META_STORE, (store) => store.get(INVENTORY_KEY));
    const db = await getDb();
    return (await db.get(BSB_META_STORE, INVENTORY_KEY)) as
      | BsbInventory
      | undefined;
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'getBsbInventory' },
    });
  }
}

export async function setBsbInventory(inventory: BsbInventory): Promise<void> {
  try {
    const db = await getDb();
    await db.put(BSB_META_STORE, inventory);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'setBsbInventory' },
    });
  }
}

export async function clearBsbInventory(): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(BSB_META_STORE, INVENTORY_KEY);
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'clearBsbInventory' },
    });
  }
}

export async function hydrateAtomic(
  books: BsbBook[],
  meta: BsbMeta,
): Promise<void> {
  try {
    const db = await getDb();
    const tx = db.transaction([BSB_BOOKS_STORE, BSB_META_STORE], 'readwrite');
    const booksStore = tx.objectStore(BSB_BOOKS_STORE);
    const metaStore = tx.objectStore(BSB_META_STORE);

    await booksStore.clear();
    for (const book of books) {
      await booksStore.put(book);
    }
    await metaStore.put(meta);
    // A completed atomic hydrate supersedes any resumable-checkpoint
    // row (2026-08-25); drop it so the store never carries a stale
    // inventory alongside a fresh meta.
    await metaStore.delete(INVENTORY_KEY);
    await tx.done;
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'hydrateAtomic', bookCount: books.length },
    });
  }
}

// ---- M.3.6.3.c retrieval helpers ----
//
// Thin wrappers over the BSB stores. `getChapter` and `getVerse` are
// the primary read path for the M.3.7 picker; `getAllBooksFromIdb` is
// consumed by `bsb-search.ts` for the lazy in-memory index. `getBook`
// is `getBsbBook` with a null-not-undefined return for API symmetry
// across the four helpers.

export async function getChapter(
  slug: string,
  chapter: number,
): Promise<BsbBlock[] | null> {
  const book = await getBsbBook(slug);
  if (!book) return null;
  return book.chapters[String(chapter)] ?? null;
}

export async function getBook(slug: string): Promise<BsbBook | null> {
  const book = await getBsbBook(slug);
  return book ?? null;
}

// Walks blocks in document order, collects every fragment whose `v`
// matches `verse`, and aggregates vCont fragments into one
// BsbVerse. Intended for verse lookup + share-card surfaces, NOT
// reader rendering: the reader path (M.3.8.b) consumes blocks
// directly so it can preserve paragraph wrapping, poetry indent,
// and section headers. The aggregation joins fragment text with a
// single space; leading or trailing punctuation on a fragment can
// produce awkward spacing, which is acceptable for lookup but not
// for reader output.
export async function getVerse(
  slug: string,
  chapter: number,
  verse: number,
): Promise<BsbVerse | null> {
  const blocks = await getChapter(slug, chapter);
  if (!blocks) return null;

  const fragments: BsbVerse[] = [];
  for (const block of blocks) {
    if (block.type === 'paragraph') {
      for (const v of block.verses) {
        if (v.v === verse) fragments.push(v);
      }
    } else if (block.type === 'poetry') {
      for (const line of block.lines) {
        for (const v of line.verses) {
          if (v.v === verse) fragments.push(v);
        }
      }
    }
  }

  if (fragments.length === 0) return null;
  if (fragments.length === 1) return fragments[0];

  // Aggregate vCont fragments. Document order is intentional and
  // preserved (Mark 9:43-46 emits verses in non-ascending source
  // order; aggregation must not reorder).
  const allOmitted = fragments.every((f) => f.omitted === true);
  if (allOmitted) {
    return { v: verse, omitted: true, t: '' };
  }

  const anySegments = fragments.some((f) => Array.isArray(f.t));
  if (!anySegments) {
    const joined = fragments
      .map((f) => (typeof f.t === 'string' ? f.t : ''))
      .join(' ');
    return { v: verse, t: joined };
  }

  // At least one fragment carries TextSegment[]. Normalize all
  // fragments to TextSegment[], insert a separator segment between
  // fragments, and concatenate into one array.
  const segments: TextSegment[] = [];
  for (let i = 0; i < fragments.length; i++) {
    if (i > 0) segments.push({ text: ' ' });
    const t = fragments[i].t;
    if (typeof t === 'string') {
      segments.push({ text: t });
    } else {
      segments.push(...t);
    }
  }
  return { v: verse, t: segments };
}

export async function getAllBooksFromIdb(): Promise<BsbBook[]> {
  try {
    if (getReadingAccessSnapshot().restricted) {
      const books = await cachedBibleRead<BsbBook[]>(BSB_BOOKS_STORE, (store) => store.getAll());
      return books.sort((a, b) => a.order - b.order);
    }
    const db = await getDb();
    const books = await db.getAll(BSB_BOOKS_STORE);
    books.sort((a, b) => a.order - b.order);
    return books;
  } catch (err) {
    throw new BibleHydrationError('idb_error', {
      cause: err,
      context: { op: 'getAllBooksFromIdb' },
    });
  }
}
